import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Divider, Grid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { patientListApiSlice } from '@/store/slices/api/patient/patientListApiSlice';
import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { useCreateReportMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import { routes } from '@/routes';

import { SignatureStatus } from '@/types/transmissionReport';
import LabelText from '@/ui/LabelText';
import CustomModal from '@/ui/Modal';
import SwitchTabs from '@/ui/SwitchTabs';

import { DeviceVendors } from '../../analytics/constants';
import { CustomFileType } from '../../PatientProfilePage/components/PatientTabs/PatientReportsTab/components/AddReportsModal';

import ManualTransmissionSection from './Components/ManualTransmissionSection';
import ReportMetaSection from './Components/ReportMetaSection';
import styles from './ReportCreateModal.module.scss';

export type ReportFormValues = {
  reportType: string;
  color: string;
  deviceId?: string;
  vendor?: string;
  type?: string;
  serialNumber?: string;
  modelName?: string;
  modelNumber?: string;
  implantDate?: string;
  patientId: string;
};

const ReportCreateModal = ({
  isOpen,
  onClose,
  patientId
}: {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
}) => {
  const [isDeviceAdded, setIsDeviceAdded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transmissionType, setTransmissionType] = useState<'remote' | 'in-clinic'>('remote');
  const [uploadedFiles, setUploadedFiles] = useState<CustomFileType[]>([]);
  const [uploadPdfLoading, setUploadPdfLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const invalidatePatientList = () => {
    dispatch(
      patientListApiSlice.util.invalidateTags([{ type: API_SLICE.PatientList, id: 'LIST' }])
    );
  };
  const [createReport] = useCreateReportMutation();
  const [_selectedInClinicFile, setSelectedInClinicFile] = useState<File | null>(null);

  const { data: patientData } = useGetPatientQuery(patientId, { refetchOnMountOrArgChange: true });

  const getSchema = (isDeviceAdded: boolean) =>
    Yup.object({
      reportType: Yup.string().required('Report type is required'),
      color: Yup.string().required('Color is required'),
      patientId: Yup.string().required('Patient ID is required'),

      deviceId: Yup.string().when([], {
        is: () => !isDeviceAdded,
        then: (schema) => schema.required('Device is required'),
        otherwise: (schema) => schema.nullable().notRequired()
      }),
      vendor: Yup.string().when('deviceId', {
        is: () => isDeviceAdded,
        then: (schema) => schema.required('Vendor is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      type: Yup.string().when('deviceId', {
        is: () => isDeviceAdded,
        then: (schema) => schema.required('Type is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      serialNumber: Yup.string().when('deviceId', {
        is: () => isDeviceAdded,
        then: (schema) => schema.required('Serial number is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      modelName: Yup.string().when('vendor', {
        is: (vendor: string) => isDeviceAdded && vendor?.toLowerCase() === 'biotronik',
        then: (schema) => schema.required('Model name is required for biotronik'),
        otherwise: (schema) => schema.notRequired()
      }),
      modelNumber: Yup.string().when('vendor', {
        is: (vendor: string) => isDeviceAdded && vendor?.toLowerCase() !== 'biotronik',
        then: (schema) => schema.required('Model number is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      implantDate: Yup.string().when('deviceId', {
        is: () => isDeviceAdded,
        then: (schema) => schema.required('Implant date is required'),
        otherwise: (schema) => schema.notRequired()
      })
    });

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    trigger,
    formState: { errors }
  } = useForm<ReportFormValues>({
    resolver: yupResolver(getSchema(isDeviceAdded)),
    mode: 'onChange',
    defaultValues: {
      reportType: '',
      color: '',
      patientId,
      deviceId: '',
      vendor: '',
      type: '',
      serialNumber: '',
      modelName: '',
      modelNumber: '',
      implantDate: ''
    }
  });

  // Re-trigger validation when device section toggles
  useEffect(() => {
    trigger();
  }, [isDeviceAdded]);

  const onSubmit = (action: 'save-close' | 'save-view') => async (values: ReportFormValues) => {
    setIsSubmitting(true);

    const fileNames = uploadedFiles.filter((e) => e.status).map((e) => e.name);
    const device = {
      id: isDeviceAdded ? undefined : values.deviceId,
      deviceId: isDeviceAdded ? undefined : values.deviceId,
      manufacturer: isDeviceAdded ? (values.vendor as DeviceVendors) : undefined,
      type: isDeviceAdded ? values.type : undefined,
      modelName: isDeviceAdded ? values.modelName : undefined,
      modelNumber: isDeviceAdded ? values.modelNumber : undefined,
      serialNumber: isDeviceAdded ? values.serialNumber : undefined,
      implantDate: isDeviceAdded ? dayjs(values.implantDate).format('YYYY-MM-DD') : undefined
    };

    try {
      // comment out for Removing ORC changes
      // if (transmissionType === 'in-clinic') {
      //   // Prepare multipart/form-data
      //   const formData = new FormData();
      //   formData.append('patientId', patientId);
      //   formData.append('transmissionType', transmissionType);
      //   formData.append('reportType', values.reportType);
      //   formData.append('alertIndication', values.color.toLowerCase());
      //   formData.append('dateOfService', dayjs().format('YYYY-MM-DD'));
      //   formData.append('deviceInfo', JSON.stringify(device));

      //   if (selectedInClinicFile) {
      //     formData.append('file', selectedInClinicFile); // ✅ actual file object
      //   } else {
      //     throw new Error('PDF file is required');
      //   }

      //   response = await createInclinicReport(formData).unwrap();
      // } else {
      const payload = {
        patientId,
        transmissionType,
        reportType: values.reportType,
        color: values.color,
        dateOfService: dayjs().format('YYYY-MM-DD'),
        device,
        files: fileNames
      };

      const response = await createReport(payload).unwrap();
      // }
      notifications.show({ title: 'Success', message: 'Report created', color: 'green' });
      reset();
      setIsDeviceAdded(false);
      if (action === 'save-view' && response?.transmissionReportId) {
        let reportDetailUrl;
        // Only redirect if not already on the patient profile page
        if (response.signatureStatus === SignatureStatus.SignatureNotRequired) {
          // Remove trailing '/*' from the pathname if present
          const patientDetailPath = routes.physician.patientDetail(patientId).replace(/\/\*$/, '');
          if (window.location.pathname.replace(/\/\*$/, '') === patientDetailPath) {
            onClose();
            invalidatePatientList();
          } else {
            // Go to patient profile if signature is not required
            navigate(patientDetailPath);
          }
        } else {
          // Otherwise, go to the transmission report detail page
          reportDetailUrl = routes.physician.transmissionReportDetail(
            response.transmissionReportId.toString()
          );
          window.location.href = reportDetailUrl;
        }
      }
      // Close modal only for 'save-close'
      if (action === 'save-close') {
        onClose();
        invalidatePatientList();
      }
    } catch (error) {
      console.error({ error });
      notifications.show({ title: 'Error', message: 'Failed to create report', color: 'red' });
    } finally {
      setIsSubmitting(false);
    }
  };
  const vendor = useWatch({ control, name: 'vendor' });

  // Check for missing required fields
  const requiredFieldsMissing = [
    ...Object.keys(errors).filter((key) => errors[key as keyof ReportFormValues]),
    ...(transmissionType !== 'in-clinic' && uploadedFiles.filter((f) => f.status).length === 0
      ? ['uploadedFiles']
      : [])
  ];

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose,
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: 'Create Report' }}
      footer={
        <div className={styles.footer}>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              setIsDeviceAdded(false);
              onClose();
            }}
            disabled={isSubmitting}>
            Cancel
          </Button>
          <div style={{ display: 'inline-block' }}>
            <Button
              color="#4FCC88"
              onClick={() => {
                handleSubmit(onSubmit('save-close'))();
              }}
              disabled={requiredFieldsMissing.length > 0 || uploadPdfLoading || isUploading}
              loading={isSubmitting}>
              Save & Close
            </Button>
          </div>
          <div style={{ display: 'inline-block' }}>
            <Button
              onClick={() => {
                handleSubmit(onSubmit('save-view'))();
              }}
              loading={isSubmitting}
              disabled={requiredFieldsMissing.length > 0 || uploadPdfLoading || isUploading}>
              Save & View
            </Button>
          </div>
        </div>
      }>
      <div className={styles.reportForm}>
        <Grid>
          <Grid.Col span={4}>
            <LabelText
              variant="regular"
              className={styles.transmissionHeading}
              label="Transmission Type"
              boxProps={{ mb: 2 }}
            />
            <SwitchTabs
              options={[
                { label: 'Remote', id: 'remote' },
                { label: 'In Clinic', id: 'in-clinic' }
              ]}
              value={transmissionType}
              onChange={(id) => {
                setTransmissionType(id as 'remote' | 'in-clinic');
                setIsDeviceAdded(false);
                setUploadedFiles([]);
                setSelectedInClinicFile(null);
                setValue('deviceId', '', { shouldValidate: true, shouldDirty: true });
              }}
            />
          </Grid.Col>
        </Grid>

        <ReportMetaSection control={control} />
        <Divider />
        <ManualTransmissionSection
          key={transmissionType}
          control={control}
          setValue={setValue}
          patientData={patientData}
          isDeviceAdded={isDeviceAdded}
          setIsDeviceAdded={setIsDeviceAdded}
          setUploadedFiles={setUploadedFiles}
          uploadedFiles={uploadedFiles}
          setUploadPdfLoading={setUploadPdfLoading}
          vendor={vendor}
          setSelectedInClinicFile={setSelectedInClinicFile}
          transmissionType={transmissionType}
          setIsUploading={setIsUploading} // Pass the setter to update isUploading state
          isUploading={isUploading}
        />
      </div>
    </CustomModal>
  );
};

export default ReportCreateModal;
